import _get from 'lodash/get'
import { List, Map } from 'immutable'
import { map as mapPromise } from 'bluebird'
import _compact from 'lodash/compact'
import { updateScreen } from '../../pm-screen/redux/actions'
import { getContentReferenceTypes } from '../../content-references'
import { getContentFiltersList } from '../../content-filters'
import { getNodeByQuery } from '../../node'
import {
  CREATE_SECTION,
  UPDATE_SECTION_DATA,
  DELETE_SECTION,
  BATCH_GET_SECTIONS_DATA,
  GET_ONBOARD_DATA,
  GET_TOPICS_DATA,
  GET_LIST_SORT_ORDER_OPTIONS,
  GET_DISPLAY_MODE_OPTIONS,
  setSectionsProcessing,
  updateSectionSuccess,
  setSectionsError,
  setSectionsData,
  setSectionData,
  batchGetSectionsData,
  setOnboardData,
  setTopicsData,
  setListSortOrderOptionsData,
  setDisplayModeOptionsData,
  setContentReferenceTypes,
  GET_CONTENT_REFERENCE_TYPES,
  setContentFilters,
  GET_CONTENT_FILTERS,
  deleteSectionProcessing,
} from './actions'
import {
  createSection,
  updateSection,
  deleteSection,
  getSection,
  getOnboard,
  getTopics,
  getListSortOrderOptions,
  getDisplayModeOptions,
} from '..'

const log = console

async function batchGetSectionsDataHandler ({ store, payload }) {
  const { config, auth } = store.getState()
  const ids = payload
  try {
    store.dispatch(setSectionsProcessing({}))
    const sections = await mapPromise(ids, async (id) => {
      try {
        return await getSection({
          auth,
          server: config.getIn(['data', 'servers', 'brooklyn']),
          sectionId: id,
        })
      } catch (error) {
        // Only log
        log.error(error, 'Error getting section')
        return undefined
      }
    })
    // Convert sections List to a Map keyed by section id
    const data = List(sections)
      .filter((v) => v) // Remove sections that were errors
      .reduce((acc, section) => acc.set(section.get('id'), section), Map())
    store.dispatch(setSectionsData({ data }))
    store.dispatch(setSectionsProcessing({ processing: false }))
  } catch (error) {
    log.error(error, 'Error batch getting sections')
    store.dispatch(setSectionsError({
      error,
    }))
  }
}

async function createSectionHandler ({ store, payload }) {
  const { config, auth } = store.getState()
  try {
    store.dispatch(setSectionsProcessing({}))
    const section = await createSection({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      data: payload.data,
    })
    if (section) {
      store.dispatch(setSectionData(section))
      const sectionId = section.get('id')
      const screenId = _get(payload, 'screen.id')
      if (sectionId && screenId) {
        const sectionIds = _get(payload, 'screen.sectionIds', [])
        store.dispatch(updateScreen({
          screenId,
          sectionIds: [...sectionIds, sectionId],
        }))
      }
    }
    alert('Section successfully created')
  } catch (error) {
    log.error(error, 'Error updating section')
    store.dispatch(setSectionsError({
      error,
    }))
  }
}

async function updateSectionDataHandler ({ store, payload }) {
  const { config, auth } = store.getState()
  const sectionId = _get(payload, 'data.sectionId')
  try {
    store.dispatch(setSectionsProcessing({}))
    await updateSection({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      data: payload.data,
      sectionId,
    })
    store.dispatch(updateSectionSuccess())
    store.dispatch(batchGetSectionsData(payload.sectionIds))
  } catch (error) {
    log.error(error, 'Error updating section')
    store.dispatch(setSectionsError({
      error,
    }))
  }
}

async function deleteSectionHandler ({ store, payload }) {
  const { config, auth } = store.getState()
  try {
    store.dispatch(deleteSectionProcessing(true))
    await deleteSection({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      sectionId: payload,
    })
    store.dispatch(deleteSectionProcessing(false))
  } catch (error) {
    log.error(error, 'Error deleting section')
    store.dispatch(deleteSectionProcessing(false))
  }
}

async function getOnboardDataHandler ({ store, payload = {} }) {
  const { config, auth } = store.getState()
  const { onboardVersion } = payload
  try {
    if (!onboardVersion) {
      return
    }
    const onboard = await getOnboard(onboardVersion, {
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
    store.dispatch(setOnboardData(onboard.remove('screenCopy')))
  } catch (error) {
    log.error(error, 'Error getting onboard data')
  }
}

async function getTopicsDataHandler ({ store }) {
  const { config, auth } = store.getState()
  try {
    const topics = await getTopics({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
    store.dispatch(setTopicsData(topics))
  } catch (error) {
    log.error(error, 'Error getting topics data')
  }
}

async function getContentReferenceTypesHandler ({ store }) {
  const { config, auth } = store.getState()
  try {
    const types = await getContentReferenceTypes({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
    store.dispatch(setContentReferenceTypes(types))
  } catch (error) {
    log.error(error, 'Error getting Content reference types')
  }
}

async function getContentFiltersHandler ({ store, payload }) {
  const { config, auth } = store.getState()
  const { contentReferenceTypesId } = payload

  try {
    const filters = await getContentFiltersList({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      filter: { contentReferenceTypesId },
    })
    const contentFilters = filters.get('contentFilters')
    const nodes = await mapPromise(contentFilters, async (filter) => {
      const contentId = filter.get('contentId')
      const contentType = filter.get('contentType')
      try {
        const ret = await getNodeByQuery({
          server: config.getIn(['data', 'servers', 'brooklyn']),
          query: { contentId, contentType },
        })
        return {
          title: ret.get('title'),
          contentId,
          contentType,
        }
      } catch (e) {
        return null
      }
    })
    store.dispatch(setContentFilters(_compact(nodes)))
  } catch (error) {
    log.error(error, 'Error getting Content filters')
    // reset content filters
    store.dispatch(setContentFilters([]))
  }
}

async function getListSortOrdersHandler ({ store }) {
  const { config, auth } = store.getState()
  try {
    const data = await getListSortOrderOptions({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
    store.dispatch(setListSortOrderOptionsData(data))
  } catch (error) {
    log.error(error, 'Error getting display mode options data')
  }
}

async function getDisplayModesHandler ({ store }) {
  const { config, auth } = store.getState()
  try {
    const data = await getDisplayModeOptions({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
    })
    store.dispatch(setDisplayModeOptionsData(data))
  } catch (error) {
    log.error(error, 'Error getting display mode options data')
  }
}

export default (store) => (next) => (action) => {
  const { type, payload } = action
  switch (type) {
    case CREATE_SECTION:
      createSectionHandler({ store, payload })
      break
    case UPDATE_SECTION_DATA:
      updateSectionDataHandler({ store, payload })
      break
    case DELETE_SECTION:
      deleteSectionHandler({ store, payload })
      break
    case BATCH_GET_SECTIONS_DATA:
      batchGetSectionsDataHandler({ store, payload })
      break
    case GET_ONBOARD_DATA:
      getOnboardDataHandler({ store, payload })
      break
    case GET_TOPICS_DATA:
      getTopicsDataHandler({ store })
      break
    case GET_LIST_SORT_ORDER_OPTIONS:
      getListSortOrdersHandler({ store })
      break
    case GET_DISPLAY_MODE_OPTIONS:
      getDisplayModesHandler({ store })
      break
    case GET_CONTENT_REFERENCE_TYPES:
      getContentReferenceTypesHandler({ store })
      break
    case GET_CONTENT_FILTERS:
      getContentFiltersHandler({ store, payload })
      break
    default:
      break
  }
  next(action)
}
